import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import ReconnectingWebSocket from "reconnecting-websocket";

import { useApp } from "@/containers/Apps/AppContext";
import {
  getStoredToken,
  handleAuthRejection,
  waitForStoredToken,
} from "@/utils/api";

export const WebSocketContext = React.createContext<any>({});

interface WebSocketContextProviderProps {
  children?: ReactNode;
  url?: string;
  onClose?: (...args: any[]) => any;
  onMessage?: (...args: any[]) => any;
  onError?: (...args: any[]) => any;
  tablePage?: any;
}

export const WebSocketContextProvider = ({
  children,
  url,
  onClose,
  onMessage,
  onError,
  tablePage,
}: WebSocketContextProviderProps) => {
  const app = useApp();
  const [connecting, setConnecting] = useState(false);
  const [socket, setSocket] = useState<any>();

  const connect = useCallback(() => {
    if (!url || socket || connecting) {
      return;
    }

    setConnecting(true);
    const protocol = app.config.apiUrl.startsWith("https://") ? "wss" : "ws";
    const host = app.config.apiUrl
      .replace("http://", "")
      .replace("https://", "")
      .replace(/\/$/, "");

    // reconnecting-websocket invokes the URL provider on every (re)connect.
    // Reading the token from localStorage here (instead of baking it into a
    // static URL) lets a rotated token propagate to reconnects — see #1206
    // 24h auth-token TTL.
    // The token is recorded so a 4001 close can tell which token was refused.
    // After a 4001 that didn't reload the page, the reconnect waits until a
    // token is stored rather than retrying forever with none. Only then:
    // some sockets (e.g. the version one) don't need a token at all.
    let connectedToken: string | undefined;
    let awaitStoredToken = false;
    const urlProvider = async () => {
      connectedToken = awaitStoredToken
        ? await waitForStoredToken()
        : getStoredToken();
      awaitStoredToken = false;
      return `${protocol}://${host}${url}?token=${connectedToken}`;
    };
    const newSocket = new ReconnectingWebSocket(urlProvider);

    // Backend closes with 4001 when the token is missing/invalid/expired.
    // Mirror the axios 401 reauth flow so an idle session that never makes
    // an HTTP request also reauths. Only the token this socket connected
    // with is dropped: if another tab has stored a fresh one meanwhile (or
    // the socket connected with none), the reconnect picks the stored one up.
    newSocket.onclose = (event) => {
      if (event.code === 4001) {
        const outcome = handleAuthRejection(connectedToken);
        if (outcome === "reloading" || outcome === "reload-pending") {
          return;
        }
        awaitStoredToken = true;
      }
      if (onClose) {
        onClose(event);
      }
    };

    newSocket.onopen = () => {
      if (onMessage) {
        newSocket.onmessage = (message) => {
          return onMessage(JSON.parse(message.data));
        };
      }

      if (onError) {
        newSocket.onerror = onError;
      }
      setSocket(newSocket);
      setConnecting(false);
    };
  }, [connecting, socket, url, onClose, onMessage, onError, app.config.apiUrl]);

  const disconnect = () => {
    if (socket) {
      socket.close();
    } else {
      console.error("Socket was not initialized");
    }
  };

  const send = (message: any) => {
    if (socket) {
      socket.send(message);
    } else {
      console.error("Socket was not initialized");
    }
  };

  const reRegisterOnMessage = (callback: any) => {
    socket.onmessage = (message) => {
      return callback(JSON.parse(message.data));
    };
  };

  useEffect(() => {
    connect();
  }, [connect]);

  return (
    <WebSocketContext.Provider
      value={{
        disconnect,
        reRegisterOnMessage,
        send,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);
